/**
 * Fullscreen Director View badge: which i2v model rendered the scene under the
 * playhead. The label prefers the exact model id (kling-v2-6, veo-3.1-…), then
 * the concrete provider, then the route provider id; hovering (or focusing)
 * the badge reveals the full effective prompt — the exact positive bytes sent
 * to the provider — from the echo-state manifest audit (journey-event
 * fallback included). Static scenes and scenes without an audit render
 * nothing, so the badge doubles as the "this shot was animated" marker.
 *
 * The reveal is a self-managed hover card (not a Radix tooltip): pointer and
 * focus events we own behave identically in the app and in the browser test
 * harness, and nothing has to portal out of the fullscreen dialog.
 */
import { useId, useState } from "react";
import { Clapperboard } from "lucide-react";

import type { SceneI2VAudit } from "./scriptStory";

const i2vModelLabel = (i2v: SceneI2VAudit): string | null => {
  const label =
    i2v.model?.trim() || i2v.concreteProvider?.trim() || i2v.provider?.trim();
  return label || null;
};

export const I2VModelBadge = ({ i2v }: { i2v?: SceneI2VAudit | null }) => {
  const [open, setOpen] = useState(false);
  const tooltipId = useId();
  // `animated === false` is the manifest's own "rendered as a still" verdict;
  // journey/summary audits may omit the flag, so only an explicit false hides.
  if (!i2v || i2v.animated === false) return null;
  const label = i2vModelLabel(i2v);
  if (!label) return null;

  const prompt = i2v.finalPositivePrompt?.trim();
  const detailParts = [i2v.provider, i2v.concreteProvider, i2v.model].filter(
    (part, index, all): part is string =>
      !!part?.trim() && all.indexOf(part) === index,
  );

  return (
    <span
      className="relative inline-block max-w-full"
      onPointerEnter={() => setOpen(true)}
      onPointerLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
    >
      <span
        tabIndex={0}
        aria-describedby={open ? tooltipId : undefined}
        data-testid="director-view-i2v-model-badge"
        className="inline-flex max-w-full cursor-default items-center gap-1.5 rounded-full border border-white/15 bg-black/70 px-2.5 py-1 font-mono text-[11px] leading-none text-white/90 backdrop-blur-sm"
      >
        <Clapperboard className="size-3 flex-none" aria-hidden="true" />
        <span className="truncate">{label}</span>
        {i2v.fallbackApplied ? (
          <span className="flex-none text-amber-300">fallback</span>
        ) : null}
      </span>
      {open ? (
        <div
          id={tooltipId}
          role="tooltip"
          data-testid="director-view-i2v-prompt-tooltip"
          className="absolute left-0 top-full z-50 mt-2 max-h-[40vh] w-[min(36rem,75vw)] overflow-y-auto rounded-md border border-white/15 bg-black/90 p-3 text-left text-xs text-white shadow-lg backdrop-blur-sm"
        >
          {detailParts.length > 0 ? (
            <p className="mb-1 font-mono text-[10px] text-white/60">
              {detailParts.join(" · ")}
              {i2v.fallbackApplied
                ? ` — fallback ${i2v.fallbackFrom ?? "?"} → ${i2v.fallbackTo ?? "?"}`
                : ""}
            </p>
          ) : null}
          {prompt ? (
            <p className="whitespace-pre-wrap">{prompt}</p>
          ) : (
            <p className="italic text-white/80">
              I2V prompt not captured (summary only).
            </p>
          )}
        </div>
      ) : null}
    </span>
  );
};
